import { useSettingsState } from "@/context/SettingsStateContext";
import { useWebsites } from "@/context/WebsitesContext";
import { FormEvent, useEffect } from "react";
import { toast } from "sonner";
import { languages } from "@/data/languages";

export default function useSettingLanguage() {
  const { currentWebsite, updateWebsite } = useWebsites();
  const {
    websiteLanguage,
    setWebsiteLanguage,
    setCustomLanguageModalOpen,
    customLanguageInput,
    setCustomLanguageInput,
  } = useSettingsState();

  // Handle language change
  const handleLanguageChange = async (language: string) => {
    if (!currentWebsite) {
      toast.error("Please select a website first");
      return;
    }

    // If they selected "other", show the modal for custom language code
    if (language === "other") {
      setCustomLanguageInput(""); // Clear previous input
      setCustomLanguageModalOpen(true);
      return;
    }

    setWebsiteLanguage(language);

    try {
      // Update website language in database
      await updateWebsite(currentWebsite.id, { language });
      const languageName = languages.find((l) => l.code === language)?.name || language;
      toast.success(`Website language updated to ${languageName}`);
    } catch (error) {
      console.error("Failed to update website language:", error);
      toast.error("Failed to update website language");
    }
  };

  // Handle custom language submission
  const handleCustomLanguageSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!customLanguageInput.trim()) {
      toast.error("Please enter a language code");
      return;
    }

    // Use the custom code, trimmed and lowercased
    const customCode = customLanguageInput.trim().toLowerCase();

    // Close modal
    setCustomLanguageModalOpen(false);

    // Apply the custom language
    setWebsiteLanguage(customCode);

    try {
      // Update website language in database
      await updateWebsite(currentWebsite!.id, { language: customCode });
      toast.success(`Website language updated to custom code: ${customCode}`);
    } catch (error) {
      console.error("Failed to update website language:", error);
      toast.error("Failed to update website language");
    }
  };

  // Function to open the custom language modal for existing custom language
  const handleEditCustomLanguage = () => {
    setCustomLanguageInput(websiteLanguage);
    setCustomLanguageModalOpen(true);
  };

  // Add a function to display the current language name
  const getCurrentLanguageName = (): string => {
    const language = languages.find((l) => l.code === websiteLanguage);
    if (language) return language.name;
    return websiteLanguage; // If it's a custom language code, display the code itself
  };

  // Effect to sync language state with current website
  useEffect(() => {
    if (currentWebsite?.language && currentWebsite.language !== websiteLanguage) {
      setWebsiteLanguage(currentWebsite.language);
    } else if (!currentWebsite?.language && websiteLanguage !== "en") {
      setWebsiteLanguage("en"); // Default to English
    }
  }, [currentWebsite, websiteLanguage]);

  return {
    handleLanguageChange,
    handleCustomLanguageSubmit,
    handleEditCustomLanguage,
    getCurrentLanguageName,
  };
}
